import { useEffect, useMemo, useState } from "react";
import useAbort from "../../../../common/hooks/use-abort";
import useIsComponentMounted from "../../../../common/hooks/use-is-component-mounted";
import useOrderOptimizerList from "../../../../common/services/order-optimizer/list/use-order-optimizer-list";
import useOrderOptimizerRailyListingFiltersDao from "./filters/use-order-optimizer-raily-listing-filters.dao";
import OrderOptimizerListParams from "../../../../common/services/order-optimizer/list/order-optimizer-list-params";
import orderOptimizerRailyListingLoadParamsFactory from "./common/order-optimizer-raily-listing-load-params.factory";
import orderOptimizerTranslationsHelper from "../../../../languages/order-optimizer-translations.helper";
import { useAppContext } from "../../../../context/app.context";
import orderOptimizerBreadcrumbsHelper from "../../common/breadcrumbs/order-optimizer-breadcrumbs.helper";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import orderOptimizerRailyListingItemFactory from "./common/order-optimizer-raily-listing-item.factory";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import OrderOptimizerRailyListingFiltersComponent from "./filters/order-optimizer-raily-listing-filters.component";
import CardComponent from "../../../../common/components/card/card.component";
import OrderOptimizerRailyListingTableComponent from "./table/order-optimizer-raily-listing-table.component";
import usePagination from "../../../../common/hooks/use-pagination";
import ListingPaginationComponent from "../../../../common/components/listing/pagination/listing-pagination.component";
import OrderOptimizerRailyListingFilter from "./common/types/order-optimizer-raily-listing-filter";
import OrderOptimizerRailyListingFilterType from "./common/types/order-optimizer-raily-listing-filter-type";
import { OrderOptimizerRailyListingItem } from "./common/types/order-optimizer-raily-listing-item";
import StatusLegendComponent from "../../../../common/components/status-legend/status-legend.component";
import orderOptimzierRailyListingHelper from "./common/order-optimizer-raily-listing.helper";

const OrderOptimizerRailyListingComponent = () => {
  const isComponentMounted = useIsComponentMounted();

  const orderOptimizerList = useOrderOptimizerList();
  const orderOptimizerListAbort = useAbort();

  const filtersDao = useOrderOptimizerRailyListingFiltersDao();

  const [filters, setFilters] = useState<OrderOptimizerRailyListingFilter[]>(
    () => filtersDao.getFilters(),
  );

  const selectedHumanId = useMemo(() => {
    const filterValue = filters.find(
      (filter) =>
        filter.type === OrderOptimizerRailyListingFilterType.ORDER_HUMAN_ID,
    )?.value as string | undefined;

    return Number.parseInt(filterValue ?? "");
  }, [filters]);

  const loadListingItems = () => {
    const params: OrderOptimizerListParams =
      orderOptimizerRailyListingLoadParamsFactory.create(
        filters,
        page,
        pageSize,
      );

    orderOptimizerList.load(params, orderOptimizerListAbort.signal);
  };

  const { page, pageSize, setPage, setPageSize } = usePagination({
    totalResults: orderOptimizerList.data.totalCount,
    defaultPageSize: filtersDao.getPageSize() ?? 10, // limit set to 10 (default 100)
    defaultPage: filtersDao.getPage(),
  });

  const saveFilters = () => {
    filtersDao.saveFilters(filters, page, pageSize);
  };

  useEffect(() => {
    if (!isComponentMounted) {
      return;
    }

    saveFilters();
  }, [filters, page, pageSize, isComponentMounted]);

  useEffect(() => {
    loadListingItems();

    return () => orderOptimizerListAbort.revoke();
  }, [page, pageSize, filters]);

  const translations =
    orderOptimizerTranslationsHelper.getRailyListingTranslations();
  const { selectedAppLanguage, setBreadcrumbs } = useAppContext();

  useEffect(() => {
    const breadcrumbs = orderOptimizerBreadcrumbsHelper.getListingBreadcrumbs();
    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage]);

  const documentTitleTranslations =
    appTranslationsHelper.getDocumentTitleTranslations();

  useDocumentTitle(documentTitleTranslations.orderOptimizerListing);

  const listingItems: OrderOptimizerRailyListingItem[] = useMemo(() => {
    return orderOptimizerRailyListingItemFactory.createListingItems(
      orderOptimizerList.data.data,
    );
  }, [orderOptimizerList.data]);

  const statusOptions = orderOptimzierRailyListingHelper.getStatusOptions();

  return (
    <>
      <HeadingComponent title={translations.header.headingLabel} />
      <StatusLegendComponent statusData={statusOptions} />
      <OrderOptimizerRailyListingFiltersComponent
        filters={filters}
        isListingLoading={orderOptimizerList.isLoading}
        onFiltersChange={(filters) => {
          setFilters(filters);
          setPage(1);
        }}
      />
      <CardComponent>
        <OrderOptimizerRailyListingTableComponent
          listingItems={listingItems}
          selectedHumanId={selectedHumanId}
          isError={orderOptimizerList.isError}
          isLoading={orderOptimizerList.isLoading}
          applyOptimizationStatus={orderOptimizerList.applyOptimizationStatus}
        />
        <ListingPaginationComponent
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          page={page}
          pageSize={pageSize}
          totalResults={orderOptimizerList.data.totalCount}
        />
      </CardComponent>
    </>
  );
};

export default OrderOptimizerRailyListingComponent;
